/* eslint complexity: ["error", {max: 14}] -- applies to entire file */
import { useMemo } from "react";

import type { ImageContentFit } from "expo-image";
import { Image as ExpoImage } from "expo-image";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import type { ImageSourcePropType } from "react-native";

import { TextTestID } from "@/constants/TestID";
import { AnimationDuration } from "@/constants/UI";
import useProprietaryHeicFiles from "@/hooks/useProprietaryHeicFiles";
import Spinner from "@/ui/core/Spinner";
import { Box } from "@/ui/layout";
import { ColorToken } from "@/ui/theme/tokens";
import { isWeb } from "@/utils/platform.util";
import { joinTestIDs } from "@/utils/string.util";

// this is re-exported as a pass-through to maintain the expectation of interacting with ui/core/Image always
// rather than invoking ExpoImage.prefetch or Image.prefetch (react-native) directly outside of the scope of this module
const { prefetch } = ExpoImage;

interface ImageSize {
  height: number;
  width: `${number}%` | number;
}

interface ImageProps extends BaseViewProps {
  borderRadius?: number;
  contentFit?: ImageContentFit;
  // HEIC images are Apple proprietary and are not supported on non-Safari browsers; but the mobile app allows HEIC upload
  // so contentType (server-reported MIME type) is passed to detect HEIC images and transcode them to JPEG when viewing/rendering them
  contentType?: string;
  isRounded?: boolean;
  size: ImageSize | number;
  transitionMs?: number;
  uriSource: ImageSourcePropType | string;
}

const Image = ({
  accessibilityLabel,
  borderRadius,
  contentFit = "cover",
  contentType,
  isRounded,
  size,
  testID,
  transitionMs = AnimationDuration.ImageTransition,
  uriSource,
}: ImageProps) => {
  // on mobile, no-op/pass-through
  // on web, converts HEIC/HEIF string URIs to a JPEG blob url
  const { isConverting, safeUri } = useProprietaryHeicFiles({
    contentType,
    uri: typeof uriSource === "string" ? uriSource : undefined,
  });
  // eslint-disable-next-line sonarjs/function-return-type -- currently reported in SonarQube as unresolved
  const widthValue = useMemo(() => {
    if (typeof size === "number") {
      return size;
    }
    return size.width;
  }, [size]);

  const heightValue = useMemo(() => {
    if (typeof size === "number") {
      return size;
    }
    return size.height;
  }, [size]);

  if (
    (isEmpty(uriSource) && !uriSource) ||
    isNil(widthValue) ||
    isNil(heightValue)
  ) {
    return null;
  }

  const resolvedBorderRadius =
    borderRadius ?? (isRounded && heightValue ? Number(heightValue) / 2 : 0);

  return (
    <Box height={heightValue} position="relative" width={widthValue}>
      <ExpoImage
        accessibilityLabel={accessibilityLabel}
        contentFit={contentFit}
        source={typeof uriSource === "string" ? { uri: safeUri } : uriSource}
        style={{
          backgroundColor: ColorToken.NeutralLine,
          borderRadius: resolvedBorderRadius,
          height: heightValue,
          width: widthValue,
        }}
        testID={joinTestIDs(testID, TextTestID.Image)}
        transition={transitionMs}
      />
      {/* on web, HEIC transcode can take a second or so; overlay a spinner so the image box isn't just blank while it converts. */}
      {isWeb() && isConverting && (
        <Box
          alignItems="center"
          backgroundColor={ColorToken.NeutralLine}
          borderRadius={resolvedBorderRadius}
          bottom={0}
          justifyContent="center"
          left={0}
          position="absolute"
          right={0}
          testID={joinTestIDs(testID, "converting-spinner")}
          top={0}
        >
          <Spinner size="small" />
        </Box>
      )}
    </Box>
  );
};

export type { ImageProps };
export { prefetch };
export default Image;
